import { useEffect, useState } from 'react';
import { X, Save, Loader2, RefreshCw, CalendarDays, Wand2, Sparkles } from 'lucide-react';
import { format, addDays } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import type { Job } from '../lib/store';
import ScheduleCheck from './ScheduleCheck';

const WORKERS = ['Sammy', 'Jim', 'Stewart'];

export default function FollowUpModal({
  parent,
  onClose,
  onBooked,
}: {
  parent: Job;
  onClose: () => void;
  onBooked: () => void;
}) {
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(format(addDays(new Date(), 3), 'yyyy-MM-dd'));
  const [time, setTime] = useState('09:00');
  const [durationHours, setDurationHours] = useState(1);
  const [assignedTo, setAssignedTo] = useState(parent.assigned_to || 'Stewart');
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // Auto-suggest a reason via AI on mount
  useEffect(() => {
    (async () => {
      setAiLoading(true);
      try {
        const res = await api.post('/ai/suggest-followup-reason', { job_id: parent.id });
        if (res.data?.reason && !reason) setReason(res.data.reason);
      } catch (e) {
        /* ignore */
      } finally {
        setAiLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent.id]);

  const submit = async () => {
    if (!reason.trim()) return toast.error('Please describe what needs doing on the follow-up');
    setSaving(true);
    try {
      // Compose title + notes with cross-reference to parent job
      const title = `Follow-up · ${parent.title}`;
      const notes = `Follow-up from ${parent.job_number || parent.title}.\nReason: ${reason.trim()}`;

      const payload: any = {
        title,
        type: parent.type,
        client_id: parent.client_id || '',
        client_address: parent.client_address || '',
        location: parent.location || { address: parent.client_address || '' },
        date,
        time,
        duration_type: 'hours',
        duration_hours: durationHours,
        assigned_to: assignedTo,
        status: 'Pending',
        notes,
      };
      const res = await api.post('/jobs', payload);
      const followUp = res.data;

      // Flip parent job to "Follow-up Visit Required" and cross-link
      await api.put(`/jobs/${parent.id}`, {
        status: 'Follow-up Visit Required',
        notes:
          (parent.notes ? parent.notes + '\n\n' : '') +
          `Follow-up booked as ${followUp.job_number} on ${format(new Date(date), 'd MMM yyyy')} at ${time}.`,
      });

      toast.success(`Follow-up ${followUp.job_number} booked for ${format(new Date(date), 'EEE d MMM')}`);
      onBooked();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to book follow-up');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-pop overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-50 grid place-items-center">
              <RefreshCw className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">Book a follow-up visit</h2>
              <p className="text-xs text-slate-500">
                Creates a new job linked to {parent.job_number || parent.title}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Context */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
            <div className="font-semibold text-slate-900">{parent.client_name || 'Client'}</div>
            <div className="text-xs text-slate-500">
              {parent.client_address || parent.location?.address || 'No address on file'}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                What needs doing on the follow-up?
              </label>
              {aiLoading && (
                <span className="text-[10px] text-purple-600 inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> AI suggesting…
                </span>
              )}
              {!aiLoading && reason && (
                <span className="text-[10px] text-purple-600 inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> AI-suggested (editable)
                </span>
              )}
            </div>
            <textarea
              autoFocus
              className="input min-h-[80px] resize-y"
              placeholder="e.g. Return with new pump, refit isolation valve, check leak has stopped…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Date
              </label>
              <input
                type="date"
                className="input"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Start time
              </label>
              <input
                type="time"
                className="input"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Est. hours
              </label>
              <input
                type="number"
                min={0.5}
                step={0.5}
                className="input"
                value={durationHours}
                onChange={(e) => setDurationHours(Number(e.target.value))}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Assigned to
            </label>
            <select
              className="input"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              {WORKERS.map((w) => (
                <option key={w}>{w}</option>
              ))}
            </select>
          </div>

          {/* Live conflict check */}
          <ScheduleCheck
            date={date}
            time={time}
            durationType="hours"
            durationHours={durationHours}
            durationDays={1}
            assignedTo={assignedTo}
            excludeJobId={parent.id}
            onAssignChange={(w) => setAssignedTo(w)}
          />
        </div>

        <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button onClick={submit} className="btn btn-primary" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
            Book follow-up
          </button>
        </div>
      </div>
    </div>
  );
}
